/**
 * Theme Presets
 * Pre-defined theme configurations for common use cases
 */

import { ThemeDefinition, ThemePreset } from '../types/theme';

/**
 * Default Dark Theme (Current TraceKit style)
 */
export const defaultDarkTheme: ThemeDefinition = {
  id: 'default-dark',
  name: 'TraceKit Dark',
  mode: 'dark',
  colors: {
    primary: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
    },
    secondary: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
    },
    success: {
      light: '#86efac',
      main: '#22c55e',
      dark: '#15803d',
    },
    warning: {
      light: '#fcd34d',
      main: '#f59e0b',
      dark: '#d97706',
    },
    error: {
      light: '#fca5a5',
      main: '#ef4444',
      dark: '#dc2626',
    },
    info: {
      light: '#93c5fd',
      main: '#3b82f6',
      dark: '#1d4ed8',
    },
    background: {
      default: '#0f172a', // slate-900
      paper: '#1e293b', // slate-800
      elevated: '#334155', // slate-700
    },
    surface: {
      main: '#1e293b',
      hover: '#334155',
      active: '#475569',
    },
    text: {
      primary: '#f1f5f9', // slate-100
      secondary: '#cbd5e1', // slate-300
      disabled: '#64748b', // slate-500
      inverse: '#0f172a', // slate-900
    },
    border: {
      light: 'rgba(255, 255, 255, 0.05)',
      main: 'rgba(255, 255, 255, 0.1)',
      dark: 'rgba(255, 255, 255, 0.2)',
    },
    sidebar: {
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
      text: '#ffffff',
      textSecondary: '#cbd5e1',
      hover: 'rgba(255, 255, 255, 0.1)',
      active: 'linear-gradient(90deg, #3b82f6 0%, #6366f1 100%)',
      border: 'rgba(255, 255, 255, 0.1)',
      accent: '#3b82f6',
    },
    header: {
      background: 'rgba(255, 255, 255, 0.05)',
      text: '#f1f5f9',
      border: 'rgba(255, 255, 255, 0.1)',
    },
    navigation: {
      background: 'transparent',
      text: '#cbd5e1',
      hover: 'rgba(255, 255, 255, 0.1)',
      active: 'linear-gradient(90deg, #3b82f6 0%, #6366f1 100%)',
      activeText: '#ffffff',
      border: 'rgba(255, 255, 255, 0.1)',
    },
  },
};

/**
 * Default Light Theme (Clean, professional)
 */
export const defaultLightTheme: ThemeDefinition = {
  id: 'default-light',
  name: 'TraceKit Light',
  mode: 'light',
  colors: {
    primary: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
    },
    secondary: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
    },
    success: {
      light: '#dcfce7',
      main: '#22c55e',
      dark: '#15803d',
    },
    warning: {
      light: '#fef3c7',
      main: '#f59e0b',
      dark: '#d97706',
    },
    error: {
      light: '#fee2e2',
      main: '#ef4444',
      dark: '#dc2626',
    },
    info: {
      light: '#dbeafe',
      main: '#3b82f6',
      dark: '#1d4ed8',
    },
    background: {
      default: '#f9fafb', // gray-50
      paper: '#ffffff',
      elevated: '#f3f4f6', // gray-100
    },
    surface: {
      main: '#ffffff',
      hover: '#f9fafb',
      active: '#f3f4f6',
    },
    text: {
      primary: '#111827', // gray-900
      secondary: '#6b7280', // gray-500
      disabled: '#9ca3af', // gray-400
      inverse: '#ffffff',
    },
    border: {
      light: '#f3f4f6',
      main: '#e5e7eb',
      dark: '#d1d5db',
    },
    sidebar: {
      background: 'linear-gradient(180deg, #1e40af 0%, #1e3a8a 100%)',
      text: '#ffffff',
      textSecondary: '#bfdbfe',
      hover: 'rgba(255, 255, 255, 0.1)',
      active: 'rgba(255, 255, 255, 0.2)',
      border: 'rgba(255, 255, 255, 0.1)',
      accent: '#60a5fa',
    },
    header: {
      background: '#ffffff',
      text: '#111827',
      border: '#e5e7eb',
    },
    navigation: {
      background: 'transparent',
      text: '#ffffff',
      hover: 'rgba(255, 255, 255, 0.1)',
      active: 'rgba(255, 255, 255, 0.2)',
      activeText: '#ffffff',
      border: 'rgba(255, 255, 255, 0.1)',
    },
  },
};

/**
 * Keystone Light Theme (Inspired by keystoneenvironmental.ca)
 * Professional teal/blue color scheme
 */
export const keystoneLightTheme: ThemeDefinition = {
  id: 'keystone-light',
  name: 'Keystone Professional',
  mode: 'light',
  colors: {
    primary: {
      50: '#ecfeff',
      100: '#cffafe',
      200: '#a5f3fc',
      300: '#67e8f9',
      400: '#22d3ee',
      500: '#06b6d4',
      600: '#0891b2',
      700: '#0e7490',
      800: '#155e75',
      900: '#164e63',
    },
    secondary: {
      50: '#f0fdfa',
      100: '#ccfbf1',
      200: '#99f6e4',
      300: '#5eead4',
      400: '#2dd4bf',
      500: '#14b8a6',
      600: '#0d9488',
      700: '#0f766e',
      800: '#115e59',
      900: '#134e4a',
    },
    success: {
      light: '#d1fae5',
      main: '#10b981',
      dark: '#059669',
    },
    warning: {
      light: '#fef3c7',
      main: '#f59e0b',
      dark: '#d97706',
    },
    error: {
      light: '#fee2e2',
      main: '#ef4444',
      dark: '#dc2626',
    },
    info: {
      light: '#e0f2fe',
      main: '#0891b2',
      dark: '#0e7490',
    },
    background: {
      default: '#ffffff',
      paper: '#f8fafc',
      elevated: '#f1f5f9',
    },
    surface: {
      main: '#ffffff',
      hover: '#f8fafc',
      active: '#f1f5f9',
    },
    text: {
      primary: '#0f172a', // slate-900
      secondary: '#475569', // slate-600
      disabled: '#94a3b8', // slate-400
      inverse: '#ffffff',
    },
    border: {
      light: '#f1f5f9',
      main: '#e2e8f0',
      dark: '#cbd5e1',
    },
    sidebar: {
      background: 'linear-gradient(180deg, #0e7490 0%, #155e75 50%, #164e63 100%)',
      text: '#ffffff',
      textSecondary: '#cffafe',
      hover: 'rgba(255, 255, 255, 0.15)',
      active: 'rgba(255, 255, 255, 0.25)',
      border: 'rgba(255, 255, 255, 0.1)',
      accent: '#14b8a6',
    },
    header: {
      background: '#ffffff',
      text: '#0f172a',
      border: '#e2e8f0',
    },
    navigation: {
      background: 'transparent',
      text: '#ffffff',
      hover: 'rgba(255, 255, 255, 0.15)',
      active: 'rgba(255, 255, 255, 0.25)',
      activeText: '#ffffff',
      border: 'rgba(255, 255, 255, 0.1)',
    },
  },
};

/**
 * Keystone Dark Theme (For dark mode option)
 */
export const keystoneDarkTheme: ThemeDefinition = {
  id: 'keystone-dark',
  name: 'Keystone Dark',
  mode: 'dark',
  colors: {
    ...keystoneLightTheme.colors,
    background: {
      default: '#0f172a',
      paper: '#1e293b',
      elevated: '#334155',
    },
    surface: {
      main: '#1e293b',
      hover: '#334155',
      active: '#475569',
    },
    text: {
      primary: '#f1f5f9',
      secondary: '#cbd5e1',
      disabled: '#64748b',
      inverse: '#0f172a',
    },
    border: {
      light: 'rgba(255, 255, 255, 0.05)',
      main: 'rgba(255, 255, 255, 0.1)',
      dark: 'rgba(255, 255, 255, 0.2)',
    },
    header: {
      background: 'rgba(255, 255, 255, 0.05)',
      text: '#f1f5f9',
      border: 'rgba(255, 255, 255, 0.1)',
    },
  },
};

/**
 * Default preset (TraceKit blue)
 */
export const defaultPreset: ThemePreset = {
  id: 'default',
  name: 'TraceKit Default',
  description: 'Default TraceKit blue theme with dark and light modes',
  light: defaultLightTheme,
  dark: defaultDarkTheme,
};

/**
 * Keystone preset (Teal/blue professional)
 */
export const keystonePreset: ThemePreset = {
  id: 'keystone',
  name: 'Keystone Professional',
  description: 'Professional teal/blue theme inspired by Keystone Environmental',
  light: keystoneLightTheme,
  dark: keystoneDarkTheme,
};

/**
 * All available presets
 */
export const themePresets: Record<string, ThemePreset> = {
  default: defaultPreset,
  keystone: keystonePreset,
};

/**
 * Get a theme preset by ID
 */
export function getThemePreset(id: string): ThemePreset | undefined {
  return themePresets[id];
}

/**
 * Get all theme preset IDs
 */
export function getThemePresetIds(): string[] {
  return Object.keys(themePresets);
}

/**
 * Get all theme presets
 */
export function getAllThemePresets(): ThemePreset[] {
  return Object.values(themePresets);
}

